/**
 * Holds the state of Visitor Attributes (features)
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var KeyPath = require('../utils/key_path');

/**
 * Map of VisitorProfileProvider to provided value
 * @typedef {Object.<*>} VisitorProfile
 */

module.exports = {
  initialize: function() {
    this.__state = {
      profile: {},
      metadata: {},
      visitorId: null,
    };

    this.on(actionTypes.SET_VISITOR_ID_VIA_API, this.__setVisitorId);
    this.on(actionTypes.SET_VISITOR_ATTRIBUTES, this.__setAttributes);
    this.on(actionTypes.LOAD_EXISTING_VISITOR_PROFILE, this.__setVisitorProfile);
  },

  /**
   * Get the current visitor profile
   * @returns {module:stores/visitor~VisitorProfile}
   * Warning: Not cloned because too expensive.
   */
  getVisitorProfile: function(): import('core/stores/visitor').VisitorProfile {
    return this.__state.profile;
  },

  /**
   * Get the current visitor profile's metadata
   * @returns {Object}
   * Warning: Not cloned because too expensive.
   */
  getVisitorProfileMetadata: function(): any {
    return this.__state.metadata;
  },

  /**
   * Get an attribute by name or key path
   * @param {string|Array<string>} attributeNameOrKeypath
   * @return {*}
   */
  getAttribute: function(attributeNameOrKeypath:string|string[]): any {
    var state = this.__state.profile;

    return _.cloneDeep(KeyPath.getFieldValue(state, attributeNameOrKeypath));
  },

  /**
   * Get an attribute's metadata
   * @param {string} attributeType
   * @return {Object|undefined}
   */
  getAttributeMetadata: function(attributeType:string): any|undefined {
    return _.cloneDeep(this.__state.metadata[attributeType]);
  },

  /**
   * @return {string} visitorId
   */
  getVisitorIdFromAPI: function(): string {
    return this.__state.visitorId;
  },

  /**
   * @param {Object} payload
   * @param {Object} payload.profile
   * @param {Object} payload.metadata
   * @private
   */
  __setVisitorProfile: function(payload:any) {
    this.__state.profile = payload.profile;
    this.__state.metadata = payload.metadata;

    this.emitChange();
  },

  /**
   * Add multiple attributes and associated metadata to the store
   * @param {Object} payload
   * @param {Array} payload.attributes
   * @param {Array<string>} payload.attributes.key
   *        Key path to the attribute.
   * @param {any} payload.attributes.value
   * @param {Object} [payload.attributes.metadata]
   *        This is merged into any existing metadata.
   * @private
   *
   * Attributes are stored at their key path like so, where '...key' denotes following
   * the key path:
   *
   * profile: {...key: value},
   *          {...key: value},
   * },
   * metadata: {...key: {metadataKey: metadataValue},
   *            ...key: {metadataKey: metadataValue},
   *           }
   * }
   *
   * For example, if the key is ['one', 'two'], value is 'wumbo', metadata is {modified: true}:
   * profile: {
   *            one: {
   *              two: 'wumbo',
   *            },
   *          },
   * metadata: {
   *             one: {
   *               two: {
   *                 modified: true,
   *               },
   *             },
   *           },
   */
  __setAttributes: function(payload:any) {
    _.each(payload.attributes, _.bind(function(attribute) {
      var keyPath = attribute.key;

      KeyPath.setFieldValue(this.__state.profile, keyPath, attribute.value);

      if (attribute.metadata) {
        _.forOwn(attribute.metadata, _.bind(function(metadataValue, metadataKey) {
          KeyPath.setFieldValue(this.__state.metadata, keyPath.concat(metadataKey), metadataValue);
        }, this));
      }
    }, this));

    this.emitChange();
  },

  /**
   * Set using "user" api when __EXPERIMENTAL_TRACKING_ONLY__ enabled
   * @param {string} visitorId
   * @private
   */
  __setVisitorId: function(payload) {
    this.__state.visitorId = payload;
    this.emitChange();
  },
};
